// Procedural pixel-art sprites. Drawn once at boot into Phaser textures so
// the game ships with zero external image dependencies (player avatar
// aside). Style: chunky pixel/iso-ish, dark navy background, violet+cyan
// accents. Every sprite is drawn at logical size, then upscaled with
// nearest-neighbour by Phaser at use-site if needed.

import Phaser from 'phaser';
import { brandHex, brand } from '../brand';

type G = Phaser.GameObjects.Graphics;

const px = (g: G, x: number, y: number, color: number, alpha = 1) => {
  g.fillStyle(color, alpha);
  g.fillRect(x, y, 1, 1);
};

const rect = (g: G, x: number, y: number, w: number, h: number, color: number, alpha = 1) => {
  g.fillStyle(color, alpha);
  g.fillRect(x, y, w, h);
};

const generate = (
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  draw: (g: G) => void,
): void => {
  if (scene.textures.exists(key)) return;
  const g = scene.add.graphics();
  g.setVisible(false);
  draw(g);
  g.generateTexture(key, w, h);
  g.destroy();
};

// ---------------------------------------------------------------------------
// Floor tile: light blue with a soft Main Blue grid, 32x32 (tiled)
// ---------------------------------------------------------------------------
const drawFloor = (g: G) => {
  rect(g, 0, 0, 32, 32, brandHex.bg);
  // soft grid using Light Blue 2 (#D9E4FF)
  rect(g, 0, 0, 32, 1, brandHex.primarySoft, 0.9);
  rect(g, 0, 0, 1, 32, brandHex.primarySoft, 0.9);
  // tiny Main Blue marker for subtle texture
  px(g, 16, 16, brandHex.primary, 0.5);
};

// ---------------------------------------------------------------------------
// Wall block: Main Blue panel with a Lighter Blue rim, 32x32
// ---------------------------------------------------------------------------
const drawWall = (g: G) => {
  rect(g, 0, 0, 32, 32, brandHex.primary);
  rect(g, 0, 0, 32, 2, brandHex.primaryAlt);
  rect(g, 0, 30, 32, 2, brandHex.bgDeep);
  // rivet marks at corners
  px(g, 4, 4, brandHex.bgDeep);
  px(g, 27, 4, brandHex.bgDeep);
  px(g, 4, 27, brandHex.bgDeep);
  px(g, 27, 27, brandHex.bgDeep);
};

// ---------------------------------------------------------------------------
// Server rack: tall black box with blinking LED strip, 32x48
// ---------------------------------------------------------------------------
const drawServerRack = (g: G) => {
  rect(g, 0, 0, 32, 48, 0x0a0d1a);
  rect(g, 1, 1, 30, 46, 0x14182d);
  // unit dividers
  for (let i = 4; i < 48; i += 6) {
    rect(g, 3, i, 26, 4, 0x1f2540);
    // LEDs
    px(g, 5, i + 1, brandHex.ok);
    px(g, 7, i + 1, brandHex.primary);
    px(g, 25, i + 1, brandHex.threat, 0.7);
  }
  // top/bottom highlight
  rect(g, 0, 0, 32, 1, 0x2a3360);
  rect(g, 0, 47, 32, 1, 0x000000);
};

// ---------------------------------------------------------------------------
// Desktop computer + monitor (top-down): keyboard, beige tower, screen
// ---------------------------------------------------------------------------
const drawDesktop = (g: G) => {
  // base / desk shadow
  rect(g, 2, 24, 28, 6, 0x000000, 0.35);
  // keyboard
  rect(g, 5, 22, 22, 6, 0x1f2640);
  rect(g, 6, 23, 20, 4, 0x2a3360);
  // monitor bezel
  rect(g, 4, 4, 24, 16, 0x0a0d1a);
  // monitor screen
  rect(g, 6, 6, 20, 12, brandHex.primary, 0.85);
  // glow lines
  rect(g, 7, 8, 18, 1, brandHex.primary, 0.9);
  rect(g, 7, 11, 14, 1, brandHex.text, 0.4);
  rect(g, 7, 13, 16, 1, brandHex.text, 0.3);
  // monitor stand
  rect(g, 14, 20, 4, 2, 0x1a2050);
};

// ---------------------------------------------------------------------------
// Crate / data box — 24x24. A small floor-standing storage box that
// gets scattered randomly through the arena as a search target.
// ---------------------------------------------------------------------------
const drawCrate = (g: G) => {
  // shadow
  rect(g, 3, 21, 18, 2, 0x000000, 0.45);
  // crate body
  rect(g, 3, 5, 18, 16, 0x2a1958);
  // top face (slight highlight)
  rect(g, 3, 5, 18, 2, brandHex.primaryAlt, 0.85);
  // metal bands
  rect(g, 3, 9, 18, 1, 0x000000, 0.5);
  rect(g, 3, 16, 18, 1, 0x000000, 0.5);
  // X-bracing on the front
  for (let i = 0; i < 14; i++) {
    px(g, 5 + i, 11 + i, brandHex.primary, 0.55);
    px(g, 18 - i, 11 + i, brandHex.primary, 0.55);
    if (i >= 14 - i) break;
  }
  // bolts (corners)
  px(g, 4, 6, brandHex.primary);
  px(g, 19, 6, brandHex.primary);
  px(g, 4, 19, brandHex.primary);
  px(g, 19, 19, brandHex.primary);
  // dim status LED
  rect(g, 11, 13, 2, 2, brandHex.asset, 0.85);
};

// ---------------------------------------------------------------------------
// Asset icons (collectibles) — 24x24, distinctive silhouettes
// ---------------------------------------------------------------------------
const drawWebserverAsset = (g: G) => {
  // tower with two LEDs
  rect(g, 6, 4, 12, 16, 0x1a2050);
  rect(g, 7, 5, 10, 14, 0x2a3360);
  rect(g, 8, 7, 8, 1, brandHex.primary);
  rect(g, 8, 10, 8, 1, brandHex.ok);
  rect(g, 8, 13, 8, 1, brandHex.asset);
  rect(g, 6, 20, 12, 2, 0x000000, 0.4);
};

const drawDomainAsset = (g: G) => {
  // globe: circle approximation with lat/long lines
  const cx = 12, cy = 12, r = 8;
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      const d = Math.sqrt(x * x + y * y);
      if (d <= r) {
        const c = d > r - 1 ? 0x1a2050 : brandHex.primary;
        px(g, cx + x, cy + y, c, d > r - 1 ? 1 : 0.85);
      }
    }
  }
  // equator + meridian
  rect(g, cx - r, cy, r * 2 + 1, 1, brandHex.bg, 0.6);
  rect(g, cx, cy - r, 1, r * 2 + 1, brandHex.bg, 0.6);
};

const drawS3Asset = (g: G) => {
  // bucket trapezoid
  rect(g, 5, 6, 14, 14, brandHex.asset);
  rect(g, 6, 5, 12, 1, brandHex.asset);
  rect(g, 7, 4, 10, 1, brandHex.asset);
  rect(g, 4, 7, 1, 12, brandHex.asset);
  rect(g, 19, 7, 1, 12, brandHex.asset);
  // shading
  rect(g, 6, 7, 12, 1, 0x000000, 0.3);
  rect(g, 7, 18, 10, 1, 0x000000, 0.4);
};

const drawApiAsset = (g: G) => {
  // brace { }
  // left brace
  rect(g, 5, 5, 3, 1, brandHex.primaryAlt);
  rect(g, 4, 6, 1, 5, brandHex.primaryAlt);
  rect(g, 3, 11, 1, 2, brandHex.primaryAlt);
  rect(g, 4, 13, 1, 5, brandHex.primaryAlt);
  rect(g, 5, 18, 3, 1, brandHex.primaryAlt);
  // right brace
  rect(g, 16, 5, 3, 1, brandHex.primaryAlt);
  rect(g, 19, 6, 1, 5, brandHex.primaryAlt);
  rect(g, 20, 11, 1, 2, brandHex.primaryAlt);
  rect(g, 19, 13, 1, 5, brandHex.primaryAlt);
  rect(g, 16, 18, 3, 1, brandHex.primaryAlt);
  // dots between
  px(g, 11, 9, brandHex.text);
  px(g, 11, 12, brandHex.text);
  px(g, 11, 15, brandHex.text);
};

const drawDatabaseAsset = (g: G) => {
  // cylinder
  for (let y = 5; y <= 19; y++) {
    rect(g, 4, y, 16, 1, brandHex.primary, 0.85);
  }
  // top ellipses (just a band)
  rect(g, 4, 4, 16, 2, brandHex.text, 0.6);
  rect(g, 4, 9, 16, 1, brandHex.bg, 0.4);
  rect(g, 4, 14, 16, 1, brandHex.bg, 0.4);
  rect(g, 4, 19, 16, 1, 0x000000, 0.5);
};

// ---------------------------------------------------------------------------
// Scanner: terminal kiosk, 96x80
// ---------------------------------------------------------------------------
const drawScanner = (g: G) => {
  // base
  rect(g, 8, 60, 80, 16, 0x0a0d1a);
  rect(g, 8, 60, 80, 2, 0x2a3360);
  // body
  rect(g, 16, 8, 64, 56, 0x111733);
  rect(g, 16, 8, 64, 2, brandHex.primary);
  rect(g, 16, 62, 64, 2, 0x000000);
  // screen
  rect(g, 20, 14, 56, 36, 0x000000);
  rect(g, 22, 16, 52, 32, brandHex.bg);
  // grid pattern on screen
  for (let i = 22; i < 74; i += 4) rect(g, i, 16, 1, 32, brandHex.primary, 0.18);
  for (let j = 16; j < 48; j += 4) rect(g, 22, j, 52, 1, brandHex.primary, 0.18);
  // SCAN label
  rect(g, 36, 53, 24, 6, 0x000000);
  rect(g, 38, 54, 4, 4, brandHex.primary);
  rect(g, 44, 54, 4, 4, brandHex.primary);
  rect(g, 50, 54, 4, 4, brandHex.primary);
  rect(g, 56, 54, 4, 4, brandHex.primary);
};

// ---------------------------------------------------------------------------
// CVE projectile: red diamond, 24x24
// ---------------------------------------------------------------------------
const drawCve = (g: G) => {
  const cx = 12, cy = 12;
  for (let y = -10; y <= 10; y++) {
    const w = 10 - Math.abs(y);
    for (let x = -w; x <= w; x++) {
      const c = Math.abs(x) === w || Math.abs(y) === 10 ? 0x7f1d1d : brandHex.threat;
      px(g, cx + x, cy + y, c);
    }
  }
  // inner highlight
  rect(g, 10, 10, 4, 4, 0xfca5a5, 0.7);
  px(g, 11, 11, 0xffffff);
};

// ---------------------------------------------------------------------------
// Player body + baked head, top-down, 24x24. The user's actual avatar
// lives in the HUD; the on-screen sprite is a generic pixel hero so we
// don't have to composite a face onto a 24-px sprite each frame.
// ---------------------------------------------------------------------------
const drawPlayerBody = (g: G) => {
  // shadow
  rect(g, 4, 20, 16, 3, 0x000000, 0.45);
  // legs
  rect(g, 8, 16, 3, 5, 0x1a2050);
  rect(g, 13, 16, 3, 5, 0x1a2050);
  // torso (hoodie)
  rect(g, 6, 10, 12, 8, brandHex.primary);
  rect(g, 6, 10, 12, 1, brandHex.primaryAlt);
  rect(g, 17, 10, 1, 8, 0x000000, 0.3);
  // arms
  rect(g, 4, 11, 2, 5, brandHex.primary);
  rect(g, 18, 11, 2, 5, brandHex.primary);
  // logo dot on chest
  px(g, 11, 14, brandHex.primary);
  px(g, 12, 14, brandHex.primary);
  // hood collar
  rect(g, 8, 9, 8, 1, brandHex.primaryAlt);
  // head (skin-neutral grey-violet so it reads as "the hero" not any one demographic)
  rect(g, 9, 3, 6, 6, 0xc9b6e6);
  rect(g, 9, 3, 6, 1, 0x8a7aaa);
  rect(g, 14, 3, 1, 6, 0x8a7aaa, 0.6);
  // helmet visor band
  rect(g, 8, 5, 8, 2, brandHex.primary, 0.85);
  rect(g, 8, 5, 8, 1, brandHex.text, 0.4);
  // helmet dome
  rect(g, 8, 1, 8, 3, brandHex.primary);
  rect(g, 8, 1, 8, 1, brandHex.primaryAlt);
  // antenna dot
  px(g, 11, 0, brandHex.threat);
};

// Same pose as playerBody but with arms raised — used when the hero just
// found / picked up an asset.
const drawPlayerCarry = (g: G) => {
  rect(g, 4, 20, 16, 3, 0x000000, 0.45);
  rect(g, 8, 16, 3, 5, 0x1a2050);
  rect(g, 13, 16, 3, 5, 0x1a2050);
  rect(g, 6, 10, 12, 8, brandHex.primary);
  rect(g, 6, 10, 12, 1, brandHex.primaryAlt);
  rect(g, 17, 10, 1, 8, 0x000000, 0.3);
  // arms raised (above shoulders, holding asset)
  rect(g, 4, 6, 2, 5, brandHex.primary);
  rect(g, 18, 6, 2, 5, brandHex.primary);
  px(g, 11, 14, brandHex.primary);
  px(g, 12, 14, brandHex.primary);
  rect(g, 8, 9, 8, 1, brandHex.primaryAlt);
  rect(g, 9, 3, 6, 6, 0xc9b6e6);
  rect(g, 9, 3, 6, 1, 0x8a7aaa);
  rect(g, 14, 3, 1, 6, 0x8a7aaa, 0.6);
  rect(g, 8, 5, 8, 2, brandHex.primary, 0.85);
  rect(g, 8, 5, 8, 1, brandHex.text, 0.4);
  rect(g, 8, 1, 8, 3, brandHex.primary);
  rect(g, 8, 1, 8, 1, brandHex.primaryAlt);
  px(g, 11, 0, brandHex.threat);
};

// ---------------------------------------------------------------------------
// Helmet/visor overlay: drawn over the avatar head, 32x32
// ---------------------------------------------------------------------------
const drawHelmet = (g: G) => {
  // top dome
  for (let y = 0; y < 8; y++) {
    const w = 16 - Math.max(0, 6 - y);
    rect(g, 16 - w, y, w * 2, 1, brandHex.primary);
  }
  // chin strap
  rect(g, 4, 24, 24, 2, brandHex.primary);
  // visor band (semi-transparent)
  rect(g, 4, 10, 24, 6, brandHex.primary, 0.55);
  rect(g, 4, 10, 24, 1, brandHex.text, 0.6);
  rect(g, 4, 15, 24, 1, 0x000000, 0.6);
  // antenna dot
  px(g, 16, 0, brandHex.threat);
};

// ---------------------------------------------------------------------------
// Robot boss: bulky, 96x128
// ---------------------------------------------------------------------------
const drawBoss = (g: G) => {
  // shadow
  rect(g, 16, 120, 64, 6, 0x000000, 0.5);
  // legs
  rect(g, 28, 96, 16, 24, 0x1a2050);
  rect(g, 52, 96, 16, 24, 0x1a2050);
  rect(g, 28, 116, 16, 4, 0x000000);
  rect(g, 52, 116, 16, 4, 0x000000);
  // torso
  rect(g, 20, 48, 56, 50, 0x2a3360);
  rect(g, 20, 48, 56, 4, 0x4a558a);
  rect(g, 20, 94, 56, 4, 0x000000);
  // chest plate with glow
  rect(g, 36, 60, 24, 24, 0x1a2050);
  rect(g, 38, 62, 20, 20, brandHex.threat, 0.85);
  rect(g, 42, 66, 12, 12, 0xfca5a5, 0.7);
  px(g, 47, 71, 0xffffff);
  px(g, 48, 71, 0xffffff);
  // arms
  rect(g, 8, 52, 12, 36, 0x2a3360);
  rect(g, 76, 52, 12, 36, 0x2a3360);
  rect(g, 6, 88, 16, 8, 0x4a558a);
  rect(g, 74, 88, 16, 8, 0x4a558a);
  // head
  rect(g, 32, 8, 32, 36, 0x2a3360);
  rect(g, 32, 8, 32, 2, 0x4a558a);
  rect(g, 34, 10, 28, 32, 0x14182d);
  // visor (single red bar)
  rect(g, 36, 22, 24, 6, brandHex.threat);
  rect(g, 36, 22, 24, 1, 0xfca5a5);
  // antennae
  rect(g, 36, 4, 2, 4, 0x4a558a);
  rect(g, 58, 4, 2, 4, 0x4a558a);
  px(g, 36, 2, brandHex.threat);
  px(g, 58, 2, brandHex.threat);
  // mouth grill
  for (let i = 38; i < 60; i += 3) rect(g, i, 36, 2, 2, 0x000000);
};

// ---------------------------------------------------------------------------
// Generic prop tag (for chair, plant, etc.) — keep it small
// ---------------------------------------------------------------------------
const drawChair = (g: G) => {
  rect(g, 8, 6, 8, 14, 0x1a2050);
  rect(g, 6, 8, 12, 4, 0x2a3360);
  rect(g, 11, 18, 2, 4, 0x000000);
  rect(g, 6, 22, 12, 1, 0x000000, 0.4);
};

const drawPlant = (g: G) => {
  // pot
  rect(g, 8, 16, 8, 8, 0x4a3826);
  rect(g, 9, 17, 6, 1, 0x6b5036);
  rect(g, 8, 23, 8, 1, 0x000000, 0.4);
  // foliage
  rect(g, 6, 6, 12, 10, brandHex.ok, 0.85);
  rect(g, 8, 4, 8, 4, brandHex.ok);
  px(g, 10, 8, brandHex.text, 0.3);
  px(g, 14, 10, brandHex.text, 0.3);
};

// ---------------------------------------------------------------------------
// 1x1 white pixel — used for solid-color sprites + tinting tricks
// ---------------------------------------------------------------------------
const drawPixel = (g: G) => {
  rect(g, 0, 0, 1, 1, 0xffffff);
};

// ---------------------------------------------------------------------------
// Code snippet projectile: 24×24, orange pixel text block
// ---------------------------------------------------------------------------
const drawCodeSnippet = (tint: number) => (g: G) => {
  // background glow
  rect(g, 2, 2, 20, 20, tint, 0.15);
  rect(g, 0, 0, 24, 24, tint, 0.08);
  // jagged code lines
  rect(g, 3, 4, 10, 2, tint, 0.9);
  rect(g, 3, 8, 16, 2, tint, 0.85);
  rect(g, 3, 12, 8, 2, tint, 0.9);
  rect(g, 3, 16, 14, 2, tint, 0.8);
  rect(g, 3, 20, 6, 2, tint, 0.85);
  // bright outline
  px(g, 0, 0, tint); px(g, 23, 0, tint); px(g, 0, 23, tint); px(g, 23, 23, tint);
};

// ---------------------------------------------------------------------------
// Boss animation frames: fallback = reuse the generic drawBoss shape
// (real PNGs loaded by BootScene will override these)
// ---------------------------------------------------------------------------
const drawBossIdle0 = (g: G) => drawBoss(g);
const drawBossIdle1 = (g: G) => {
  drawBoss(g);
  // subtle visor blink
  rect(g, 36, 22, 24, 6, 0xff5555);
};
const drawBossThrow0 = (g: G) => {
  drawBoss(g);
  // raise left arm
  rect(g, 8, 36, 12, 12, 0x2a3360);
};
const drawBossThrow1 = (g: G) => {
  drawBoss(g);
  rect(g, 6, 30, 14, 12, 0x2a3360);
};
const drawBossAdvance0 = (g: G) => {
  drawBoss(g);
  rect(g, 20, 52, 56, 50, 0x3a4470);
};
const drawBossAdvance1 = (g: G) => {
  drawBoss(g);
  rect(g, 20, 52, 56, 50, 0x4a5580);
};

// ---------------------------------------------------------------------------
// IONIX powerup: 24×24 blue diamond with white centre. Yellow halo is
// rendered separately (particle ring) at the BossScene callsite.
// ---------------------------------------------------------------------------
const drawPowerupIonix = (g: G) => {
  const cx = 12, cy = 12;
  // diamond
  for (let y = -9; y <= 9; y++) {
    const w = 9 - Math.abs(y);
    for (let x = -w; x <= w; x++) {
      const edge = Math.abs(x) === w || Math.abs(y) === 9;
      px(g, cx + x, cy + y, edge ? brandHex.bgDeep : brandHex.primary, edge ? 1 : 0.9);
    }
  }
  // bright centre
  rect(g, 10, 10, 4, 4, 0xffffff, 0.5);
  px(g, 12, 12, 0xffffff, 0.9);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const SPRITE_KEYS = {
  pixel: 'pixel',
  floor: 'tile_floor',
  wall: 'tile_wall',
  serverRack: 'prop_server_rack',
  serverRackB: 'prop_server_rack_b',
  serverRackC: 'prop_server_rack_c',
  serverRackD: 'prop_server_rack_d',
  desktop: 'prop_desktop',
  chair: 'prop_chair',
  plant: 'prop_plant',
  scanner: 'prop_scanner',
  crate: 'prop_crate',
  coffee: 'prop_coffee',
  printer: 'prop_printer',
  locker: 'prop_locker',
  whiteboard: 'prop_whiteboard',
  trash: 'prop_trash',
  monitorWall: 'prop_monitor_wall',
  floorMat: 'prop_floor_mat',
  door: 'prop_door',
  switch: 'prop_switch',
  cable: 'prop_cable',
  cve: 'cve',
  playerBody: 'player_body',
  playerCarry: 'player_carry',
  helmet: 'helmet',
  boss: 'boss',
  bossIdle0: 'boss_idle_0',
  bossIdle1: 'boss_idle_1',
  bossThrow0: 'boss_throw_0',
  bossThrow1: 'boss_throw_1',
  bossAdvance0: 'boss_advance_0',
  bossAdvance1: 'boss_advance_1',
  bossPhase3_1: 'boss_phase3_1',
  bossPhase3_2: 'boss_phase3_2',
  exploitedMarker: 'exploited_marker',
  codeSnippet0: 'code_snippet_0',
  codeSnippet1: 'code_snippet_1',
  powerupIonix: 'powerup_ionix',
  asset: {
    webserver: 'asset_webserver',
    domain: 'asset_domain',
    s3: 'asset_s3',
    api: 'asset_api',
    database: 'asset_database',
    cloud: 'asset_cloud',
    cert: 'asset_cert',
    subdomain: 'asset_subdomain',
    email: 'asset_email',
    dns: 'asset_dns',
    dataleak: 'asset_dataleak',
  },
} as const;

export const generateAllSprites = (scene: Phaser.Scene): void => {
  generate(scene, SPRITE_KEYS.pixel, 1, 1, drawPixel);
  generate(scene, SPRITE_KEYS.floor, 32, 32, drawFloor);
  generate(scene, SPRITE_KEYS.wall, 32, 32, drawWall);
  generate(scene, SPRITE_KEYS.serverRack, 32, 48, drawServerRack);
  generate(scene, SPRITE_KEYS.desktop, 32, 32, drawDesktop);
  generate(scene, SPRITE_KEYS.chair, 24, 24, drawChair);
  generate(scene, SPRITE_KEYS.plant, 24, 24, drawPlant);
  generate(scene, SPRITE_KEYS.scanner, 96, 80, drawScanner);
  generate(scene, SPRITE_KEYS.crate, 24, 24, drawCrate);
  generate(scene, SPRITE_KEYS.cve, 24, 24, drawCve);
  generate(scene, SPRITE_KEYS.playerBody, 24, 24, drawPlayerBody);
  generate(scene, SPRITE_KEYS.playerCarry, 24, 24, drawPlayerCarry);
  generate(scene, SPRITE_KEYS.helmet, 32, 32, drawHelmet);
  generate(scene, SPRITE_KEYS.boss, 96, 128, drawBoss);
  generate(scene, SPRITE_KEYS.bossIdle0, 96, 128, drawBossIdle0);
  generate(scene, SPRITE_KEYS.bossIdle1, 96, 128, drawBossIdle1);
  generate(scene, SPRITE_KEYS.bossThrow0, 96, 128, drawBossThrow0);
  generate(scene, SPRITE_KEYS.bossThrow1, 96, 128, drawBossThrow1);
  generate(scene, SPRITE_KEYS.bossAdvance0, 96, 128, drawBossAdvance0);
  generate(scene, SPRITE_KEYS.bossAdvance1, 96, 128, drawBossAdvance1);
  generate(scene, SPRITE_KEYS.codeSnippet0, 24, 24, drawCodeSnippet(0xff8c00));
  generate(scene, SPRITE_KEYS.codeSnippet1, 24, 24, drawCodeSnippet(0xffd700));
  generate(scene, SPRITE_KEYS.powerupIonix, 24, 24, drawPowerupIonix);
  generate(scene, SPRITE_KEYS.asset.webserver, 24, 24, drawWebserverAsset);
  generate(scene, SPRITE_KEYS.asset.domain, 24, 24, drawDomainAsset);
  generate(scene, SPRITE_KEYS.asset.s3, 24, 24, drawS3Asset);
  generate(scene, SPRITE_KEYS.asset.api, 24, 24, drawApiAsset);
  generate(scene, SPRITE_KEYS.asset.database, 24, 24, drawDatabaseAsset);
};

export { brand };
